import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  HairlineCard,
  Icons,
  iconSize,
  MenuItem,
  palette,
  Stack,
  StatusChip,
  statusTone,
  TextField,
  Typography,
  fontStacks,
} from '@munifocus/ui';
import {
  COUNTER_PAYMENT_METHODS,
  TICKET_SERVICE_METHODS,
  ticketBalanceCents,
  type CounterPaymentMethod,
  type IssueTicketDto,
  type TicketListItem,
  type TicketServiceMethod,
} from '@munifocus/contracts-gov';
import { parseMoneyToCents } from '@munifocus/billing';
import { useEvidence } from '../lib/evidence-api';
import { formatDateTime } from '../lib/relative-time';
import {
  downloadTicketDocument,
  formatMoney,
  offenceLabel,
  ticketActions,
  useFileTickets,
  useIssueTicket,
  useTicketAction,
  useTicketOptions,
  type TicketAction,
} from '../lib/tickets-api';
import { useMutationToast } from '../lib/mutation-toast';

/**
 * Tickets on this file: the list, issuing a new one, and every move after.
 *
 * Renders nothing at all when the caller can neither read nor issue tickets
 * here, or when the file's municipality has not turned tickets on and none
 * exist — a town without the module sees no trace of it on its files.
 */
export function TicketsPanel({
  fileId,
  mayIssue = true,
}: {
  fileId: string;
  /**
   * Whether the caller holds `ticket:issue` on this file, from the file's
   * session access. False skips the issue-form request: the API answers it
   * with a 404 to anyone without the permission, and a clerk who may read the
   * file's tickets collected one on every file page.
   */
  mayIssue?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const tickets = useFileTickets(fileId);
  const options = useTicketOptions(fileId, mayIssue);
  const [issuing, setIssuing] = useState(false);
  const [acting, setActing] = useState<{ ticket: TicketListItem; kind: TicketAction['kind'] }>();
  const toast = useMutationToast();

  const canIssue = options.isSuccess && options.data.enabled;
  const hasTickets = (tickets.data?.length ?? 0) > 0;
  if (!canIssue && !hasTickets) return null;

  async function download(ticket: TicketListItem, format: 'docx' | 'pdf') {
    try {
      await downloadTicketDocument(ticket, format);
    } catch (err) {
      toast.error(err, 'gov.tickets.downloadError');
    }
  }

  return (
    <HairlineCard sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
        >
          <Typography
            component="h2"
            sx={{ fontFamily: fontStacks.display, fontWeight: 700, fontSize: 16 }}
          >
            {t('gov.tickets.panelTitle')}
          </Typography>
          {canIssue && (
            <Button
              variant="contained"
              startIcon={<Icons.Receipt size={iconSize.sm} />}
              onClick={() => setIssuing(true)}
            >
              {t('gov.tickets.issue')}
            </Button>
          )}
        </Stack>

        {tickets.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} thickness={5} sx={{ color: palette.brand.accent }} />
          </Box>
        )}
        {tickets.isSuccess && !hasTickets && (
          <Typography variant="body2" color="text.secondary">
            {t('gov.tickets.empty')}
          </Typography>
        )}

        {tickets.data?.map((ticket) => (
          <Box
            key={ticket.id}
            sx={{ border: `1px solid ${palette.border.line}`, borderRadius: 1, p: 1.5 }}
          >
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600 }}>{ticket.reference}</Typography>
                <StatusChip
                  status={statusTone.ticket[ticket.status]}
                  label={t(`gov.tickets.status.${ticket.status}`)}
                />
                <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                  {formatMoney(ticket.amountCents, ticket.currency, i18n.language)}
                </Typography>
              </Stack>
              <Typography variant="body2">
                {ticket.offenceCode} — {offenceLabel(ticket, i18n.language)}
                {ticket.bylawSection ? ` (${ticket.bylawSection})` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('gov.tickets.issuedBy', {
                  name: ticket.issuedByName ?? t('gov.tickets.unknownOfficer'),
                  date: formatDateTime(ticket.issuedAt, i18n.language),
                })}
                {ticket.dueAt &&
                  ` · ${t('gov.tickets.dueOn', { date: formatDateTime(ticket.dueAt, i18n.language) })}`}
                {ticket.amountPaidCents > 0 &&
                  ` · ${t('gov.tickets.balance', {
                    amount: formatMoney(ticketBalanceCents(ticket), ticket.currency, i18n.language),
                  })}`}
              </Typography>
              {ticket.disputeReason && ticket.status === 'disputed' && (
                <Typography variant="body2" color="text.secondary">
                  {t('gov.tickets.disputeReason', { reason: ticket.disputeReason })}
                </Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', pt: 0.5 }} useFlexGap>
                <Button size="small" onClick={() => download(ticket, 'pdf')}>
                  {t('gov.tickets.downloadPdf')}
                </Button>
                <Button size="small" onClick={() => download(ticket, 'docx')}>
                  {t('gov.tickets.downloadWord')}
                </Button>
                {ticketActions(ticket).map((kind) => (
                  <Button
                    key={kind}
                    size="small"
                    variant="outlined"
                    onClick={() => setActing({ ticket, kind })}
                  >
                    {t(`gov.tickets.action.${kind}`)}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>

      {issuing && options.data && (
        <IssueTicketDialog
          fileId={fileId}
          onClose={() => setIssuing(false)}
          schedule={options.data.schedule}
          recipient={options.data.recipient}
        />
      )}
      {acting && (
        <TicketActionDialog
          ticket={acting.ticket}
          kind={acting.kind}
          onClose={() => setActing(undefined)}
        />
      )}
    </HairlineCard>
  );
}

function IssueTicketDialog({
  fileId,
  schedule,
  recipient,
  onClose,
}: {
  fileId: string;
  schedule: NonNullable<ReturnType<typeof useTicketOptions>['data']>['schedule'];
  recipient: NonNullable<ReturnType<typeof useTicketOptions>['data']>['recipient'];
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const issue = useIssueTicket(fileId);
  const evidence = useEvidence(fileId);
  const [entryId, setEntryId] = useState(schedule[0]?.id ?? '');
  const [name, setName] = useState(recipient.name ?? '');
  const [email, setEmail] = useState(recipient.email ?? '');
  const [mailing, setMailing] = useState(recipient.mailingAddress ?? '');
  const [locale, setLocale] = useState<'en' | 'fr'>(recipient.locale);
  const [notes, setNotes] = useState('');
  const [serveNow, setServeNow] = useState(false);
  const [method, setMethod] = useState<TicketServiceMethod>('personal');
  const [evidenceIds, setEvidenceIds] = useState<string[]>([]);
  // One key per opening of the form: a double submit returns the same ticket.
  const clientRequestId = useMemo(() => crypto.randomUUID(), []);
  const entry = schedule.find((e) => e.id === entryId);

  function submit() {
    if (!entry) return;
    const dto: IssueTicketDto = {
      fineScheduleEntryId: entry.id,
      clientRequestId,
      recipientName: name.trim() || undefined,
      recipientEmail: email.trim() || undefined,
      recipientMailingAddress: mailing.trim() || undefined,
      recipientLocale: locale,
      notes: notes.trim() || undefined,
      evidenceIds: evidenceIds.length ? evidenceIds : undefined,
      servedBy: serveNow ? method : undefined,
    };
    issue.mutate(dto, { onSuccess: onClose });
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.tickets.issueTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {schedule.length === 0 ? (
            <Alert severity="info">{t('gov.tickets.noSchedule')}</Alert>
          ) : (
            <TextField
              select
              label={t('gov.tickets.offence')}
              value={entryId}
              onChange={(e) => setEntryId(e.target.value)}
              fullWidth
            >
              {schedule.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.code} — {offenceLabel(e, i18n.language)} (
                  {formatMoney(e.amountCents, 'CAD', i18n.language)})
                </MenuItem>
              ))}
            </TextField>
          )}
          {entry && (
            <Typography variant="body2" color="text.secondary">
              {t('gov.tickets.entrySummary', {
                section: entry.bylawSection || '—',
                days: entry.paymentDays,
              })}
            </Typography>
          )}
          <TextField
            label={t('gov.tickets.recipientName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('gov.tickets.recipientEmail')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            fullWidth
          />
          <TextField
            label={t('gov.tickets.recipientMailing')}
            value={mailing}
            onChange={(e) => setMailing(e.target.value)}
            fullWidth
          />
          <TextField
            select
            label={t('gov.tickets.recipientLanguage')}
            value={locale}
            onChange={(e) => setLocale(e.target.value as 'en' | 'fr')}
          >
            <MenuItem value="en">{t('gov.tickets.languageEn')}</MenuItem>
            <MenuItem value="fr">{t('gov.tickets.languageFr')}</MenuItem>
          </TextField>
          {(evidence.data?.length ?? 0) > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('gov.tickets.evidence')}
              </Typography>
              {evidence.data?.map((ev) => (
                <FormControlLabel
                  key={ev.id}
                  control={
                    <Checkbox
                      checked={evidenceIds.includes(ev.id)}
                      onChange={(e) =>
                        setEvidenceIds((ids) =>
                          e.target.checked ? [...ids, ev.id] : ids.filter((id) => id !== ev.id),
                        )
                      }
                    />
                  }
                  label={`${ev.originalFilename} · ${formatDateTime(ev.capturedAt, i18n.language)}`}
                />
              ))}
            </Stack>
          )}
          <TextField
            label={t('gov.tickets.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox checked={serveNow} onChange={(e) => setServeNow(e.target.checked)} />
            }
            label={t('gov.tickets.serveNow')}
          />
          {serveNow && <ServiceMethodField value={method} onChange={setMethod} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('gov.tickets.cancel')}</Button>
        <Button variant="contained" disabled={!entry || issue.isPending} onClick={submit}>
          {t('gov.tickets.issueConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ServiceMethodField({
  value,
  onChange,
}: {
  value: TicketServiceMethod;
  onChange: (m: TicketServiceMethod) => void;
}) {
  const { t } = useTranslation();
  return (
    <TextField
      select
      label={t('gov.tickets.serviceMethod')}
      value={value}
      onChange={(e) => onChange(e.target.value as TicketServiceMethod)}
    >
      {TICKET_SERVICE_METHODS.map((m) => (
        <MenuItem key={m} value={m}>
          {t(`gov.tickets.method.${m}`)}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** One dialog for every follow-up move; each asks only for what it records. */
export function TicketActionDialog({
  ticket,
  kind,
  onClose,
}: {
  ticket: TicketListItem;
  kind: TicketAction['kind'];
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const act = useTicketAction();
  const balance = ticketBalanceCents(ticket);
  const [text, setText] = useState('');
  const [method, setMethod] = useState<TicketServiceMethod>('personal');
  const [payMethod, setPayMethod] = useState<CounterPaymentMethod>('cheque');
  const [amount, setAmount] = useState('');
  const [outcome, setOutcome] = useState<'upheld' | 'rejected'>('rejected');
  const clientRequestId = useMemo(() => crypto.randomUUID(), []);

  useEffect(() => {
    setAmount((balance / 100).toFixed(2));
  }, [balance]);

  const cents = parseMoneyToCents(amount, i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA');
  const needsText = kind === 'dispute' || kind === 'void' || kind === 'decide';
  const valid =
    (!needsText || text.trim().length >= 3) &&
    (kind !== 'payment' || (cents !== null && cents > 0 && cents <= balance));

  function buildAction(): TicketAction {
    const note = text.trim();
    switch (kind) {
      case 'serve':
        return { kind, dto: { method } };
      case 'dispute':
        return { kind, dto: { reason: note } };
      case 'decide':
        return { kind, dto: { outcome, note } };
      case 'void':
        return { kind, dto: { reason: note } };
      case 'collections':
        return { kind, dto: { note: note || undefined } };
      case 'payment':
        return {
          kind,
          dto: {
            method: payMethod,
            amountCents: cents as number,
            reference: note || undefined,
            clientRequestId,
          },
        };
    }
  }

  function submit() {
    const action = buildAction();
    act.mutate({ id: ticket.id, action }, { onSuccess: onClose });
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t(`gov.tickets.action.${kind}`)} — {ticket.reference}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(`gov.tickets.actionHint.${kind}`)}
          </Typography>
          {kind === 'serve' && <ServiceMethodField value={method} onChange={setMethod} />}
          {kind === 'decide' && (
            <TextField
              select
              label={t('gov.tickets.outcome')}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as 'upheld' | 'rejected')}
            >
              <MenuItem value="rejected">{t('gov.tickets.outcomeRejected')}</MenuItem>
              <MenuItem value="upheld">{t('gov.tickets.outcomeUpheld')}</MenuItem>
            </TextField>
          )}
          {kind === 'payment' && (
            <>
              <TextField
                select
                label={t('gov.tickets.paymentMethod')}
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value as CounterPaymentMethod)}
              >
                {COUNTER_PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {t(`gov.tickets.paymentMethods.${m}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('gov.tickets.amount')}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                helperText={t('gov.tickets.balance', {
                  amount: formatMoney(balance, ticket.currency, i18n.language),
                })}
                error={amount !== '' && (cents === null || cents <= 0 || cents > balance)}
              />
            </>
          )}
          {kind !== 'serve' && (
            <TextField
              label={t(`gov.tickets.textLabel.${kind}`)}
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline={kind !== 'payment'}
              minRows={kind !== 'payment' ? 3 : undefined}
              slotProps={{ htmlInput: { maxLength: kind === 'payment' ? 120 : 2000 } }}
              required={needsText}
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('gov.tickets.cancel')}</Button>
        <Button variant="contained" disabled={!valid || act.isPending} onClick={submit}>
          {t(`gov.tickets.action.${kind}`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
